/**
 * Evacuation Order / Alert detail panel (#204, Hazard group → "Evacuation Orders
 * & Alerts"). A raw ingested operational signal from the `arcgis_public_features`
 * table (GeoBC / Emergency Management BC, migration 0063) — NOT an actionable
 * incident: evacuation areas are authoritative context that paints the
 * who-must-move picture.
 *
 * Headlines the order/alert name + register (Order vs Alert), with the affected
 * POPULATION / HOMES (the operational payoff), hazard type, issuing agency, and an
 * OGL-Canada attribution footer. Colour is always paired with the register label
 * (§3A.7).
 */
import { useEffect, useState } from 'react';
import type { ArcgisFeature, ArcgisFeatureDetail } from '@tsr/shared-types';
import { fetchArcgisFeatureDetail } from './api';
import { evacuationStyle } from './styles/evacuationStyles';
import { logAction } from './lib/operatorActions';
import { ConnectedEvacOrderLinks } from './ConnectedEvacOrderLinks';
import { LinkToIncidentControl } from './LinkToIncidentControl';

function agoLabel(iso: string | null): string {
  if (!iso) return 'unknown';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function whenLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleString()} · ${agoLabel(iso)}`;
}

function num(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString();
}

export function EvacuationOrderBody({ feature }: { feature: ArcgisFeature }): JSX.Element {
  const p = feature.properties;
  const style = evacuationStyle(p.severity);
  const [detail, setDetail] = useState<ArcgisFeatureDetail | null>(null);

  useEffect(() => {
    logAction('view_evacuation_order_detail', { feature_id: feature.id, severity: p.severity });
  }, [feature.id, p.severity]);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchArcgisFeatureDetail(feature.id, ctl.signal)
      .then(setDetail)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [feature.id]);

  return (
    <div className="px-4 py-4 space-y-4" data-testid="evacuation-order-body">
      <div className="border-l-2 pl-3" style={{ borderColor: style.color }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          Evacuation {p.severity === 'order' ? 'order' : p.severity === 'alert' ? 'alert' : 'area'}
        </p>
        <h2 className="text-base font-semibold">{p.name || 'Unnamed evacuation area'}</h2>
        <span
          className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] px-2 py-0.5 rounded font-medium"
          style={{ backgroundColor: `${style.color}22`, color: style.color }}
        >
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: style.color }} />
          {style.label}
        </span>
      </div>

      {/* The operational payoff — affected population + homes. */}
      <section className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Population</p>
          <p className="text-xl font-semibold tabular-nums">{num(p.population)}</p>
        </div>
        <div className="rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Homes</p>
          <p className="text-xl font-semibold tabular-nums">{num(p.homes)}</p>
        </div>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Details</h3>
        <dl className="grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-400">Hazard</dt>
          <dd>{p.event_type || '—'}</dd>
          <dt className="text-slate-400">Status</dt>
          <dd className="capitalize">{p.status}</dd>
          <dt className="text-slate-400">Issuing agency</dt>
          <dd>{detail?.properties.issuing_agency || '—'}</dd>
          <dt className="text-slate-400">Declared</dt>
          <dd className="text-xs">{whenLabel(detail?.properties.event_start_at ?? null)}</dd>
          <dt className="text-slate-400">Updated</dt>
          <dd className="text-xs">{whenLabel(p.observed_at)}</dd>
          <dt className="text-slate-400">Centroid</dt>
          <dd className="font-mono text-xs">
            {p.lat.toFixed(3)}, {p.lon.toFixed(3)}
          </dd>
        </dl>
      </section>

      {/* R2/#478 — this evacuation is a PROTECTIVE ACTION; link the GeoBC order to the
          incident it protects (founder decision #2). Auto-links by proximity; unlinkable. */}
      <LinkToIncidentControl
        kind="arcgis_feature"
        alertId={feature.id}
        incidentId={p.incident_id ?? null}
        linkMode={p.incident_link_mode ?? null}
      />

      {/* #206 — contextual cross-link to the incident(s) this order threads to and
          the communities its polygon covers (renders only when a link exists). */}
      <ConnectedEvacOrderLinks featureId={feature.id} />

      <p className="text-[11px] text-slate-400 leading-relaxed">
        The affected area is drawn as an {style.label.toLowerCase()} polygon on the map (visible at
        regional zoom). Verify the current order/alert with the issuing authority before acting.
      </p>

      <footer className="border-t border-slate-800 pt-3 text-[10px] text-slate-500 leading-relaxed space-y-0.5">
        {detail?.properties.ingested_at && <div>Ingested {agoLabel(detail.properties.ingested_at)}</div>}
        {detail?.properties.source_url && (
          <div className="truncate">
            Source:{' '}
            <a
              href={detail.properties.source_url}
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-slate-300"
            >
              {detail.properties.feed_key} FeatureServer
            </a>
          </div>
        )}
        <div>{p.attribution}</div>
      </footer>
    </div>
  );
}
